import { ChangeDetectorRef, NgZone, OnDestroy, Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'dateAgo',
  standalone: true,
  pure: false,
})
export class DateAgoPipe implements PipeTransform, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService);

  private timer: number | undefined = undefined;

  transform(value: string | Date | null): string {
    if (!value) {
      return '';
    }

    this.removeTimer();
    const date = new Date(value);
    const now = new Date();

    if (isNaN(date.getTime())) {
      return '';
    }

    const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
    const isFuture = seconds < 0;
    const absSeconds = Math.abs(seconds);

    // Set up the timer for updates
    const timeToUpdate = this.getSecondsUntilUpdate(absSeconds) * 1000;
    this.timer =
      this.ngZone.runOutsideAngular(() =>
        window?.setTimeout(() => {
          this.ngZone.run(() => this.changeDetectorRef.markForCheck());
        }, timeToUpdate),
      ) ?? null;

    const result = this.getRelativeTimeString(absSeconds, isFuture);
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  ngOnDestroy(): void {
    this.removeTimer();
  }

  private getRelativeTimeString(seconds: number, isFuture: boolean): string {
    const prefix = isFuture ? this.translate.instant('date-ago.in') : '';
    const suffix = isFuture ? '' : this.translate.instant('date-ago.ago');

    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30.416);
    const years = Math.floor(days / 365);

    if (years > 0) {
      const yearText = years === 1 ? this.translate.instant('date-ago.year') : this.translate.instant('date-ago.years');
      return `${prefix} ${years} ${yearText} ${suffix}`;
    } else if (months >= 2) {
      const monthText = months === 1 ? this.translate.instant('date-ago.month') : this.translate.instant('date-ago.months');
      return `${prefix} ${months} ${monthText} ${suffix}`;
    } else if (days >= 34) {
      return `${prefix} ${this.translate.instant('date-ago.about_a_month')} ${suffix}`;
    } else if (days >= 2) {
      const dayText = days === 1 ? this.translate.instant('date-ago.day') : this.translate.instant('date-ago.days');
      return `${prefix} ${days} ${dayText} ${suffix}`;
    } else if (hours >= 24) {
      return `${prefix}${this.translate.instant('date-ago.about_a_day')} ${suffix}`;
    } else if (hours >= 2) {
      const hourText = hours === 1 ? this.translate.instant('date-ago.hour') : this.translate.instant('date-ago.hours');
      return `${prefix} ${hours} ${hourText} ${suffix}`;
    } else if (minutes >= 45) {
      return `${prefix} ${this.translate.instant('date-ago.about_an_hour')} ${suffix}`;
    } else if (minutes >= 2) {
      const minuteText = minutes === 1 ? this.translate.instant('date-ago.minute') : this.translate.instant('date-ago.minutes');
      return `${prefix} ${minutes} ${minuteText} ${suffix}`;
    } else if (minutes >= 1) {
      return `${prefix} ${this.translate.instant('date-ago.about_a_minute')} ${suffix}`;
    } else {
      return `${prefix} ${this.translate.instant('date-ago.a_few_seconds')} ${suffix}`;
    }
  }

  private removeTimer(): void {
    if (this.timer !== null) {
      window?.clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private getSecondsUntilUpdate(seconds: number): number {
    if (seconds < 60) {
      return 2;
    } // < 1 min: update every 2s
    else if (seconds < 3600) {
      return 30;
    } // < 1 hour: update every 30s
    else if (seconds < 86400) {
      return 300;
    } // < 1 day: update every 5m
    else {
      return 3600;
    } // >= 1 day: update every hour
  }
}
